import type { NiceModalHandler } from '@ebay/nice-modal-react';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';

export type DeleteConfirmation =
  | { level: 'Level1' }
  | { level: 'Level2'; confirm: string }
  | { level: 'Level3'; confirm: string; acknowledgement: string };

export interface DeleteConfirmModalProps {
  modal: NiceModalHandler<Record<string, unknown>>;
  heading: string;
  description?: string | React.ReactNode;
  confirmation: DeleteConfirmation;
  isLoading?: boolean;
  onDelete: () => Promise<void>;
}

export const DeleteConfirmModal = (props: DeleteConfirmModalProps) => {
  const { heading, description, isLoading, confirmation, modal, onDelete } = props;
  const { hide, remove, visible } = modal;

  const [value, setValue] = React.useState('');
  const [acknowledged, setAcknowledged] = React.useState(false);

  const isDisabled = (() => {
    switch (confirmation.level) {
      case 'Level1':
        return false;
      case 'Level2':
        return value !== confirmation.confirm.trim();
      case 'Level3':
        return value !== confirmation.confirm.trim() || !acknowledged;
    }
  })();

  const onClick = React.useCallback(() => {
    void onDelete().then(() => hide());
  }, [hide, onDelete]);

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle title={heading} />
          <ChakraV3.Dialog.Body>
            <ChakraV3.Stack gap={4}>
              {description}
              {confirmation.level !== 'Level1' && (
                <>
                  <ChakraV3.Text>
                    Please type <strong>"{confirmation.confirm.trim()}"</strong> to confirm
                    deletion. This action <strong>cannot</strong> be undone, and the data will be{' '}
                    <strong>permanently deleted</strong>.
                  </ChakraV3.Text>
                  <ChakraV3.Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={confirmation.confirm}
                  />
                </>
              )}
              {confirmation.level === 'Level3' && (
                <ChakraV3.HStack align='start'>
                  <ChakraV3.Checkbox
                    checked={acknowledged}
                    onChange={() => setAcknowledged((v) => !v)}
                    colorPalette='brand'
                    mt={1}
                  />
                  <ChakraV3.Text>{confirmation.acknowledgement}</ChakraV3.Text>
                </ChakraV3.HStack>
              )}
              <ChakraV3.Button
                onClick={onClick}
                loading={isLoading}
                disabled={isDisabled}
                size='sm'
                key='submit'
                colorPalette='red'
                loadingText='Deleting...'
                type='submit'
                w='full'
              >
                I understand, delete this resource.
              </ChakraV3.Button>
            </ChakraV3.Stack>
          </ChakraV3.Dialog.Body>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
};
